import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { priceService as service } from '@services/priceService';
import type { Asset } from '@shared/types';

// Local structural types (compatible with providers)
interface PriceData {
  price: number;
  timestamp: number;
}

export const getPriceService = () => {
  return service;
};

export function usePrice(symbol: Asset) {
  const queryClient = useQueryClient();

  // Use React Query for loading state and cache management
  const query = useQuery({
    queryKey: ['price', symbol],
    queryFn: async () => {
      // Ensure the symbol is bootstrapped
      await service.ensureSymbolBootstrapped(symbol);

      // After bootstrap/ensure, read synchronously
      const symbolPrice = service.getPrice(symbol) as PriceData | undefined;
      if (!symbolPrice) {
        throw new Error(`No price available for ${symbol} after bootstrap`);
      }
      return symbolPrice;
    },
    staleTime: 1000,
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  // Subscribe to real-time price updates and sync to React Query cache
  useEffect(() => {
    if (query.isLoading || query.isError) {
      return;
    }
    const unsubscribe = service.subscribe(() => {
      const symbolPrice = service.getPrice(symbol) as PriceData | undefined;
      if (symbolPrice) {
        // Update React Query cache with new data
        queryClient.setQueryData(['price', symbol], symbolPrice);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [symbol, queryClient, query.isLoading, query.isError]);

  return query;
}
